 
import Zone from "@insite/client-framework/Components/Zone";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import { COLORS } from "@tCentro/theme/design";
import * as React from "react";
/* Custom Code */
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import Block, { BlockProps, BlockVariant, BlockVariantSide } from "@tCentro/Components/Common/Block";
import CENPDPBlock from "@tCentro/Components/Common/Block/CENPDPBlock";
import blockFullWidthField from "@tCentro/Components/Common/Block/fields/fullWidth";
import gridVerticalGapField, {
    getVerticalGapData,
    VerticalGap,
} from "@tCentro/Components/Common/Block/fields/gridVerticalGap";
import blockPreserveSpacingField from "@tCentro/Components/Common/Block/fields/preserveSpacing";
import blockVariantField from "@tCentro/Components/Common/Block/fields/variant";
import blockVariantSideField from "@tCentro/Components/Common/Block/fields/variantSide";
import { gridGapStyles } from "@tCentro/theme/mixins";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Custom Code */

const mapStateToProps = (state: ApplicationState) => ({
    isProductDetailPage: getCurrentPage(state).type === "ProductDetailsPage",
});

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    cssClass = "cssClass",
    // Custom Code
    blockPreserveSpacing = "blockPreserveSpacing",
    blockFullWidth = "blockFullWidth",
    blockVariant = "blockVariant",
    blockVariantSide = "blockVariantSide",
    gridVerticalGap = "gridVerticalGap",
    paddingBlockPreserveSpacing = "paddingBlockPreserveSpacing",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.cssClass]: string;
        // Custom Code
        [fields.blockPreserveSpacing]: boolean;
        [fields.blockFullWidth]: boolean;
        [fields.blockVariant]: BlockVariant;
        [fields.blockVariantSide]: BlockVariantSide;
        [fields.gridVerticalGap]: VerticalGap;
        [fields.paddingBlockPreserveSpacing]: boolean;
    };
}

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [6, 6, 3, 3, 3],
    4: [6, 6, 4, 4, 4],
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const Row: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: { columns, gap, alignment, reflow },
        id,
        isProductDetailPage,
    } = props;

    const [isFixedBottom, setIsFixedBottom] = React.useState(false);
    const qtyRef = React.useRef<HTMLDivElement | null>(null);
    React.useEffect(() => {
        if (typeof window === "undefined") { return; }

        const isMobile = () => window.innerWidth <= 768;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (isMobile()) {
                    setIsFixedBottom(!entry.isIntersecting); // Out of view? Fix it
                }
            },
            { threshold: 0 }
        );

        const target = document.querySelector(".pdpAddCart_QtyButtons") as HTMLDivElement;
        if (target) {
            qtyRef.current = target;
            observer.observe(target);
        }

        return () => {
            if (qtyRef.current) { observer.unobserve(qtyRef.current); }
        };
    }, []);


    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem key={x} width={reflow ? convertToResponsive(columns[x]) : columns[x]} align={align}>
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    /* Custom Code */
    const {
        fields: { blockPreserveSpacing, blockFullWidth, blockVariant, blockVariantSide, gridVerticalGap, cssClass, paddingBlockPreserveSpacing },
    } = props;

    const blockProps: BlockProps = {
        preserveSpacing: blockPreserveSpacing,
        fullWidth: blockFullWidth,
        variant: blockVariant,
        variantSide: blockVariantSide,
        paddingBlockPreserveSpacing,
    };


    const gridContainerProps = {
        gap: gridVerticalGap ? 0 : gap || 0,
        css: css`
            ${gridVerticalGap ? gridGapStyles([gap ? `${gap}px` : 0], getVerticalGapData(gridVerticalGap)) : null}
            @media (min-width: 992px) and (max-width: 1199px) {
                .pdp-expert-section-tablet .fullWidthMobile {
                    flex-basis: 50% !important;
                    max-width: 50% !important;
                }
            }
            &.savedOrdersAndMylistsHeight {
                margin-bottom: 40px;
                > div[class^="GridOffset"] {
                    > div[class^="GridItemStyle"] {
                        min-height: 260px;
                        > div[class^="Wrapper"] {
                            height: 100%;
                            > div {
                                height: 100%;
                            }
                        }
                    }
                }
            }
            &.mobile_pdpQtySection{
                display: none;
            }
            &.mobile_pdpQtySection.fixed-bottom-bar {
                display: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        right: 20px;
                        position: fixed;
                        bottom: 0px;
                        z-index: 9;
                        left: 0;
                        right: 0;
                        background: #fff;
                        padding: 15px;
                        display: block;
                        background: #F2F2F2;
                        box-shadow: 0px -4px 8px 0px #2625253D;
                        > div[class^="GridOffset"] {
                            margin-bottom: 18px !important;
                        }
                        `,
                    css`
                        right: 20px;
                        position: fixed;
                        bottom: 0px;
                        z-index: 9;
                        left: 0;
                        right: 0;
                        background: #fff;
                        padding: 15px;
                        display: block;
                        background: #F2F2F2;
                        box-shadow: 0px -4px 8px 0px #2625253D;
                        > div[class^="GridOffset"] {
                            margin-bottom: 18px !important;
                        }
                        `,
                    null,
                    null,
                    null,
                ])}
                @media (max-width: 420px){
                    div[class^="GridOffset"]{
                        flex-wrap: nowrap;
                        justify-content: space-between;
                    }
                }
                div[class^="GridItemStyle"] {
                    max-width: max-content;
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                                align-items: flex-end;
                            `,
                    css`
                                align-items: flex-end;
                            `,
                    css`
                                align-items: flex-end;
                            `,
                    null,
                    null,
                ])}
                }
                div[class^="GridItemStyle"]:nth-child(2) {
                    align-items: center;
                    margin-top: 8px;
                }
            }
            &.pdpAddCart_QtyButtons {
                // position: relative;
                // right: 44px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                            right: 20px;
                        `,
                    css`
                            right: 20px;
                        `,
                    null,
                    null,
                    null,
                ])}
                @media (max-width: 420px){
                    div[class^="GridOffset"]{
                        flex-wrap: nowrap;
                        justify-content: space-between;
                    }
                }
                div[class^="GridItemStyle"] {
                    max-width: max-content;
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                                align-items: flex-end;
                            `,
                    css`
                                align-items: flex-end;
                            `,
                    css`
                                align-items: flex-end;
                            `,
                    null,
                    null,
                ])}
                }
                div[class^="GridItemStyle"]:nth-child(2) {
                    align-items: center;
                    margin-top: 8px;
                }
            }
            .needHelpSection {
                margin-bottom: 0px;
                div[class^="GridOffset"] {
                    align-items: flex-start;
                    text-align: left;
                    margin: 0px;
                    > div[class^="GridItemStyle"] {
                        padding-top: 0px;
                        padding-bottom: 0px;
                        margin-top: 0px;
                        margin-bottom: 0px;
                        padding-left: 0px;
                    }
                }
                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                                margin-bottom: 0;
                            `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            }
            .reviewActionButton {
                padding: 15px 20px 0px 0px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                            display: none;
                        `,
                    css`
                            display: none;
                        `,
                    css`
                            display: none;
                        `,
                    css`
                            display: none;
                        `,
                ])}
            }
            .mobile-change-order div[class^="GridOffset"] {
                div[class^="GridItemStyle"]:first-child div[class^="Wrapper"] {
                    margin-top: 48px;
                    margin-bottom: 104px;
                    padding-top: 10px;

                    @media (min-width: 992px) and (max-width: 1199px) {
                        border-right: 2px solid ${COLORS.background.grayShade};
                        padding-right: 30px !important;
                    }
                    @media (min-width: 1200px) {
                        border-right: 2px solid ${COLORS.background.grayShade};
                        padding-right: 64px !important;
                    }
                    ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                                margin-bottom: 56px;
                            `,
                    css`
                                margin-bottom: 56px;
                            `,
                    css`
                                margin-bottom: 76px;
                            `,
                    null,
                    null,
                ]);
            }}
                }

                ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                                div[class^="GridItemStyle"]:first-child {
                                    order: 2;
                                }
                                div[class^="GridItemStyle"]:last-child {
                                    order: 1;
                                }
                            `,
                        null,
                    ],
                    "max",
                );
            }}
            }
            &.featureProductSection {
                div[class^="Wrapper"] {
                    height: 100%;
                }
            }
            &.featurePreimerBrandProductSection {
                div[class^="Wrapper"] {
                    height: 100%;
                }
                width: 90%;
                margin-left: auto;
                margin-right: auto;
                @media (min-width: 992px) and (max-width: 1200px) {
                    width: 97%;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                            width: 100%;
                        `,
                    css`
                            width: 97%;
                        `,
                    css`
                            width: 97%;
                        `,
                    null,
                    null,
                ])}
            }
            .signInRowContainer {
                margin-bottom: 104px;
                margin-top: 32px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                            margin-bottom: 56px;
                        `,
                    css`
                            margin-bottom: 56px;
                        `,
                    css`
                            margin-bottom: 78px;
                        `,
                    null,
                    null,
                ])}
            }
            &.contactUs {
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css`
                            > div[class^="GridOffset"] {
                                div[class^="GridItemStyle"] {
                                    &:first-child {
                                        display: none;
                                    }
                                }
                            }
                        `,
                    null,
                    null,
                ])}
            }

            &.signInMainParentContainer {
                margin-top: 26px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                            margin-bottom: 8px;
                        `,
                    css`
                            margin-bottom: 8px;
                        `,
                    null,
                    null,
                    null,
                ])}
            }
            &.createAccountRowContainer {
                margin-bottom: 104px;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                            margin-bottom: 56px;
                        `,
                    css`
                            margin-bottom: 56px;
                        `,
                    css`
                            margin-bottom: 78px;
                        `,
                    null,
                    null,
                ])}
            }

            &.taxonomyCarousel {
                margin-top: 16px !important;
                > div[class^="GridOffset"]:first-child {
                    > div[class^="GridItemStyle"]:first-child {
                        @media (min-width: 992px) and (max-width: 1024px) {
                            flex-basis: 70% !important;
                            max-width: 70% !important;
                        }
                    }
                    > div[class^="GridItemStyle"]:nth-child(2) {
                        @media (min-width: 992px) and (max-width: 1024px) {
                            flex-basis: 30% !important;
                            max-width: 30% !important;
                        }
                    }
                    ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                                gap: 16px;
                            `,
                    css`
                                gap: 16px;
                            `,
                    css`
                                gap: 16px;
                            `,
                    null,
                    null,
                ])}
                }
                div[class^="Wrapper"] {
                    height: 100%;
                }
            }
        &.topLokkingExpertSection{
            > div[class^="GridOffset"]{
                > div[class^="GridItemStyle"]:nth-child(2){
                    > div[class^="Wrapper"] {
                        div[data-test-selector="flexZone_1"]{
                             div{
                                text-align: center;
                                align-items: center;
                             }
                        }
                    }
                }
            }
        }
        `,
    };

    return isProductDetailPage ? (
        <>
            <CENPDPBlock {...blockProps}>
                <GridContainer
                    className={`${cssClass} ${cssClass === "mobile_pdpQtySection" && isFixedBottom ? "fixed-bottom-bar" : ""}`}
                    {...gridContainerProps}
                >
                    {columnElements}
                </GridContainer>
            </CENPDPBlock>
        </>
    ) : (
        <Block {...blockProps}>
            <GridContainer className={cssClass} {...gridContainerProps}>
                {columnElements}
            </GridContainer>
        </Block>
    );
    /* Custom Code */
};
const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(Row),
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            /* Custom Code */
            {
                ...blockVariantField,
                name: fields.blockVariant,
                sortOrder: 100,
                tab: advancedTab,
            },
            {
                ...blockVariantSideField,
                name: fields.blockVariantSide,
                sortOrder: 101,
                tab: advancedTab,
            },
            {
                ...gridVerticalGapField,
                name: fields.gridVerticalGap,
                tooltip: `${gridVerticalGapField.tooltip} (Default: Uses the \"Gap\" configuration)`,
                sortOrder: 102,
                tab: advancedTab,
            },
            {
                ...blockFullWidthField,
                name: fields.blockFullWidth,
                sortOrder: 103,
                tab: advancedTab,
            },
            {
                ...blockPreserveSpacingField,
                name: fields.blockPreserveSpacing,
                sortOrder: 104,
                tab: advancedTab,
            },
            {
                name: fields.paddingBlockPreserveSpacing,
                displayName: "Preserve padding spacing when nested",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 105,
                tab: advancedTab,
            },
            /* Custom Code */
        ],
    },
};

export default widgetModule;
